import { useQueryClient } from 'react-query';
import { useContext } from 'react';
import { NotificationsContext } from '../../../common/notifications/Notifications';
import { useUpdateCustomRole } from './useUpdateCustomRole';
import {
  CustomRoleDto,
  CustomRolePermissionDto,
  CustomRolePermissionDtoWithoutAliasDto,
} from '../../../api-client';

export type RemoveAttributeFunction = (updateCustomRoleDto: CustomRolePermissionDto) => void;

type UseRemoveAttributeFacade = (
  id: string,
  customRole?: CustomRoleDto,
) => {
  removeAttribute: RemoveAttributeFunction;
  isRemovingAttribute: boolean;
};

export const useRemoveAttributeFacade: UseRemoveAttributeFacade = (id, customRole) => {
  const queryClient = useQueryClient();
  const { showNotification } = useContext(NotificationsContext);

  const onSuccess = () => {
    queryClient.invalidateQueries([`custom-role-${id}`]).then();
    showNotification('Attribute successfully removed');
  };

  const onError = () => showNotification('An error occurred while removing an attribute');

  const { mutate, isLoading: isRemovingAttribute } = useUpdateCustomRole({
    onError,
    onSuccess,
  });

  if (!customRole) {
    return {
      removeAttribute: () => void 0,
      isRemovingAttribute: false,
    };
  }

  const removeAttribute = (
    updateCustomRolePermissionDto: CustomRolePermissionDtoWithoutAliasDto,
  ) => {
    const updatedPermissions = customRole.permissions
      .map((permission) => {
        return { ...permission, alias: undefined };
      })
      .filter((permission) => permission.id !== updateCustomRolePermissionDto.id);

    mutate({
      id: customRole.id,
      updateCustomRoleDto: {
        name: customRole.name,
        fixedBindings: customRole.fixedBindings,
        permissions: [...updatedPermissions, updateCustomRolePermissionDto],
      },
    });
  };

  return {
    removeAttribute,
    isRemovingAttribute,
  };
};
